import type { Theme } from '@mui/material'
import { alpha } from '@mui/material'
import { createTheme, lighten } from '@mui/material/styles'

export const cssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim()

export const getTheme = (kiosk: boolean, isAdmin: boolean, isGallery: boolean, tokens: Theme['tokens']) =>
  createTheme({
    tokens,
    palette: {
      border: alpha(tokens.accentColor, 0.1),
      divider: tokens.btnActiveBgColor,
      primary: {
        main: kiosk ? 'rgba(255,255,255,1)' : tokens.btnActiveBgColor,
        contrastText: tokens.primaryColor
      },
      secondary: {
        main: kiosk ? 'rgba(255,255,255,1)' : tokens.secondaryBg,
        light: kiosk ? 'rgba(255,255,255,1)' : lighten(tokens.secondaryBg, 0.25),
        dark: '#000000'
      },
      error: {
        main: tokens.errorColor,
        light: lighten(tokens.errorColor, 0.25)
      },
      success: {
        main: tokens.successColor
      },
      text: {
        primary: tokens.primaryColor,
        secondary: '#aaa'
      },
      star: {
        main: tokens.starColor,
        dark: alpha(tokens.starColor, 0.12)
      }
    },
    spacing: (factor: number) => `${8 * factor}px`,
    typography: {
      fontSize: 12,
      fontFamily: kiosk
        ? 'var(--font-family), -apple-system, BlinkMacSystemFont, \'Segoe UI\', \'Oxygen\', \'Ubuntu\', \'Cantarell\', \'Fira Sans\', \'Droid Sans\', \'Helvetica Neue\', sans-serif'
        : 'var(--font-family), \'Raleway\', \'Roboto\', -apple-system, BlinkMacSystemFont, \'Segoe UI\', \'Oxygen\', \'Ubuntu\', \'Cantarell\', \'Fira Sans\', \'Droid Sans\', \'Helvetica Neue\', sans-serif',
      h1: {
        fontSize: '9rem',
        fontWeight: 200,
        letterSpacing: 0,
        '@media (max-width: 1181px)': {
          fontSize: '7rem'
        }
      },
      h2: {
        fontSize: '3.5rem',
        fontWeight: 300,
        letterSpacing: 0,
        '@media (max-width: 1181px)': {
          fontSize: '3rem'
        }
      },
      h3: {
        fontSize: '2.5rem',
        fontWeight: 300,
        letterSpacing: 0,
        '@media (max-width: 1181px)': {
          fontSize: '2rem'
        }
      },
      // Admin page title (YisHeader) — DS 44px / 400; rem so it scales at 4K.
      h4: {
        fontSize: '2.75rem',
        fontWeight: 400,
        letterSpacing: 0
      },
      h5: {
        fontSize: '1.5rem',
        letterSpacing: 0
      },
      h6: {
        fontSize: '1.2rem',
        letterSpacing: 0
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 500
      },
      body2: {
        fontWeight: 300,
        fontSize: '0.8rem',
        letterSpacing: 0
      },
      caption: {
        fontSize: '0.75rem',
        whiteSpace: 'pre-line'
      }
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              color: '#aaa',
              opacity: 1
            }
          }
        }
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: 46,
            gap: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: tokens.btnRadius * 2,
            overflow: 'hidden', // important: clips inner buttons,

            '& .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton': {
              border: 'none'
            },

            // Scoped "chip" variant for the InfoChannel config dialogs: separate
            // bordered 40px chips (not the segmented box above). Opt in with
            // className="yis-chip-toggle".
            '&.yis-chip-toggle': {
              border: 'none',
              borderRadius: 0,
              overflow: 'visible',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              width: 'max-content',

              '& .MuiToggleButtonGroup-grouped': {
                margin: 0,
                height: 40,
                padding: '0 16px',
                textTransform: 'uppercase',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: tokens.btnRadius * 2
              },

              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderColor: theme.palette.primary.main
              },

              // Read-only chips (e.g. values managed elsewhere): keep the chip
              // outline, just muted, so the labels stay legible.
              '& .MuiToggleButtonGroup-grouped.Mui-disabled': {
                borderColor: 'rgba(255, 255, 255, 0.12)'
              }
            }
          })
        }
      },

      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: 'none',
            textTransform: 'none',
            color: theme.palette.text.primary,
            padding: '6px 24px',
            minWidth: '60px',

            '&.Mui-selected': {
              borderLeft: 'none',
              backgroundColor: `${theme.tokens.panelHi} !important`,
              color: theme.palette.text.primary
            },

            '&.Mui-selected:hover, &:hover': {
              backgroundColor: `${theme.tokens.panelHi} !important`
            },

            // The palette has no dark `mode`, so MUI's default disabled text is
            // black-alpha — invisible on the dark panels. Dim, but keep legible.
            '&.Mui-disabled': {
              color: theme.palette.text.secondary
            }
          })
        }
      },
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) =>
            ({
              borderRadius: 12,
              borderColor: alpha(tokens.accentColor, 0.12),
              '--DataGrid-rowBorderColor': theme.palette.border,
              // Transparent body so the dark page bg shows through (prototype); only the header is panelHi.
              backgroundColor: 'transparent',
              color: 'var(--text-color)',

              '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer': {
                backgroundColor: theme.tokens.panelHi
              },
              // Design system table header typography (14 / 700, Title-Case, 0.9 white).
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: '0.8rem',
                fontWeight: 700,
                color: alpha(tokens.primaryColor, 0.9)
              },
              // MUI gives these header icon-buttons `background.paper` (white here) to
              // mask the title as they slide in on hover — match the header instead so
              // there is no white box and it adapts per theme. Scoped under
              // `.MuiDataGrid-columnHeader` to match MUI's own 3-class specificity.
              '& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton, & .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton': {
                backgroundColor: theme.tokens.panelHi
              },

              '& .MuiDataGrid-main, & .MuiDataGrid-virtualScroller, & .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner, & .MuiDataGrid-filler': {
                backgroundColor: 'transparent'
              },
              '& .MuiDataGrid-footerContainer, & .MuiDataGrid-container--top, & .MuiDataGrid-container--bottom, & .MuiDataGrid-scrollbarFiller': {
                backgroundColor: 'transparent !important'
              },
              '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]': {
                backgroundColor: 'transparent !important'
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: 'transparent',
                borderTop: `1px solid ${theme.palette.border}`
              },
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                borderColor: alpha(tokens.accentColor, 0.08)
              },
              '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.border
              },
              // striped rows
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: alpha(tokens.accentColor, 0.015)
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: `${alpha(tokens.accentColor, 0.08)} !important`
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: `${alpha(tokens.btnActiveBgColor, 0.3)} !important`
              },
              '& .MuiDataGrid-row.Mui-selected:hover': {
                backgroundColor: `${alpha(tokens.btnActiveBgColor, 0.3)} !important`
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none'
              },
              '& .MuiDataGrid-cell': {
                cursor: 'default',
                alignContent: 'center'
              },
              '& .MuiDataGrid-cell--editable': {
                cursor: 'text',
                '&:hover': {
                  backgroundColor: 'var(--submenu-tab-color)'
                }
              },
              '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                outline: 'none'
              },
              '.MuiTablePagination-toolbar p': {
                margin: 0
              },
              '.MuiTablePagination-actions': {
                display: 'flex'
              }
            })
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            // Design system input: subtle fill, 1px .3 border, radius 4, focus → white.
            height: 46,
            // The fixed 46px is single-line only; a multiline TextField (textarea,
            // e.g. YisTextArea / YisTextField multiline) must grow with its content
            // (otherwise it's clamped to one line). MUI also moves padding onto the
            // root for multiline, which stacks on top of the `input` override below
            // and over-insets the textarea; keep the single-line model (no root
            // padding, padding on the input) so the textarea aligns with a single
            // line field.
            '&.MuiInputBase-multiline': {
              height: 'auto',
              padding: 0,
              // Match the single-line fields: the global index.css [type] reset makes
              // typed inputs 0.5rem / weight 400, but a textarea (no `type`) escapes it
              // and keeps the theme's input styling — so pin it here. Target the visible
              // textarea only (the hidden autosize shadow is aria-hidden and must keep
              // its 0 vertical padding for correct height measurement).
              '& textarea:not([aria-hidden])': {
                padding: '0.5rem',
                fontWeight: 400
              }
            },
            borderRadius: tokens.btnRadius,
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.accentColor,
              borderWidth: '1px'
            }
          },
          input: {
            // 0.875rem horizontal = MUI default (14px), so the floating-label notch stays aligned.
            padding: '0.6875rem 0.875rem',
            boxShadow: 'none !important',
            background: 'transparent !important',
            border: 'none !important'
          },
          notchedOutline: {
            borderColor: alpha(tokens.accentColor, 0.3)
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            // The DataGrid column (sort/filter) menu is a portaled Popper
            // (`.MuiDataGrid-menu`, outside `.MuiDataGrid-root`) whose Paper is
            // otherwise un-themed → white. `&` binds to this Paper; scoped so only
            // the DataGrid menu is affected. Adapts per theme via the tokens.
            '.MuiDataGrid-menu &': {
              backgroundColor: tokens.secondaryBg,
              backgroundImage: 'none',
              border: `1px solid ${alpha(tokens.accentColor, 0.3)}`
            }
          },
          elevation1: {
            backgroundColor: isAdmin ? tokens.secondaryBg : 'initial'
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            overflowY: 'inherit',
            backgroundColor: isGallery && !isAdmin ? '#000000' : isAdmin ? tokens.secondaryBg : 'initial'
          },
          paperWidthSm: {
            width: isGallery && !isAdmin ? '100vw' : '50vw',
            maxWidth: 'initial',
            maxHeight: isGallery && !isAdmin ? '100vh' : 'initial'
          },
          root: {
            zIndex: 999999999,
            backdropFilter: 'blur(5px)'
          },
          container: {
            zIndex: 'inherit'
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          // change name from selectMenu to select
          select: {
            height: '100%',
            // padding: 0,
            display: 'flex',
            alignItems: 'center'
          },
          root: {
            backgroundColor: 'transparent',
            '& .MuiSvgIcon-root': {
              color: 'var(--primary-color)'
            }
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: (() => {
            if (!isAdmin) {
              return {
                background: 'var(--layer-upper-bg)',
                backgroundColor: '#06375250!important',
                backdropFilter: 'blur(6px)'
              }
            }
            return {}
          })()
        }
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: '#1c3d63',
            width: 400,
            height: 65
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            // Height/padding in rem so buttons scale with the 24px root font at ≥2000px (4K).
            height: 46,
            padding: '0.6875rem 1.125rem',
            // Design system: uppercase (MUI default) · 600 · .6px · radius 4 · disabled .4
            borderRadius: tokens.btnRadius,
            fontWeight: 600,
            letterSpacing: '0.6px',
            '&.Mui-disabled': { opacity: 0.4 }
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: (() => {
            if (isAdmin) {
              return {
                backgroundColor: 'var(--secondary-bg)'
              }
            }
            return {}
          })()
        }
      },
      MuiMenu: {
        styleOverrides: {
          list: (() => {
            if (isAdmin) {
              return {
                backgroundColor: 'var(--secondary-bg)'
              }
            }
            return {}
          })()
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            color: 'rgba(255, 255, 255, 1)'
          },
          indicator: {
            backgroundColor: 'rgb(255, 255, 255)'
          },
          scroller: (() => {
            if (!isAdmin) {
              return {
                display: 'flex',
                // `safe center` keeps tabs centred when they fit, but falls back to
                // start-alignment when they overflow - otherwise centring pushes the
                // first tab off-screen (clipped behind the scroll button) and breaks
                // the scroll buttons' enabled/disabled state.
                justifyContent: 'safe center',
                alignItems: 'center'
              }
            }
            return {}
          })()
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: 'var(--btn-main-color)',
            opacity: '1 !important',
            zIndex: 1000000000
          },
          tooltipPlacementTop: {
            zIndex: 100000000
          },
          popper: {
            zIndex: 10000000000
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          marginDense: {
            marginBottom: 'var(--marginDenseBottom)',
            marginTop: 'var(--marginDenseTop)'
          },
          marginNormal: {
            marginTop: '.5rem',
            marginBottom: '.5rem'
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          input: {
            boxShadow: 'none'
          },
          root: {
            '& .MuiSvgIcon-root': {
              color: 'var(--primary-color)'
            }
          }
        }
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            height: 'auto'
          }
        }
      },
      MuiPopper: {
        styleOverrides: {
          root: {
            '& button': {
              margin: '0 2px !important'
            },
            '& .MuiPickersPopper-paper': {
              background: 'var(--secondary-bg)'
            },
            '& .MuiSvgIcon-root': {
              color: 'var(--primary-color)'
            }
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: 'var(--primary-color)'
            }
          }
        }
      },
      MuiModal: {
        styleOverrides: {
          root: {
            zIndex: '1300 !important'
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: 'var(--primary-color)'
          }
        }
      }
    }
  })
